
import React, { useCallback, useState } from 'react';
import { styled } from '@mui/material';
import { RootState, useAppSelector } from '@/state/store';
import Popover from '@mui/material/Popover';
import { useNavigate } from 'react-router-dom';
import NotificationsIcon from '@/components/icons/Notifications';
import { markNotificationIsDelivered, sendNotificationsIsDelivered } from '@/actions/common';
import { Notification as NotificationType } from '@/state/types/notification';
import { createSelector } from '@reduxjs/toolkit';
import t from '@/utils/translate';

const NotificationsPanel = styled('div')(() => ({
}));

const ToggleButton = styled('button')(({ hasNewNotifications, adminStyle }: { hasNewNotifications: boolean, adminStyle?: boolean }) => ({
  border: 'none',
  background: 'none',
  padding: 4,
  borderRadius: 6,
  margin: 0,
  width: 32,
  height: 32,
  position: 'relative',
  ...(adminStyle
    ? { color: 'inherit' }
    : { color: hasNewNotifications ? '#091E42' : '#C1C7D0' }
  ),
  cursor: 'pointer',
  ':hover': (
    adminStyle
    ? {
      color: 'inherit',
      background: '#3b5b92'
    }
    : {
      color: '#0052CC'
    }
  ),
  '&.active, &:active': (
    adminStyle
    ? {
      background: '#3b5b92',
    }
    : {
      boxShadow: '1px 1px 1px 0px #6B778C40 inset',
      background: '#FAFAFA',
      color: '#0052CC'
    }
  )
}));

const Coin = styled('div')(() => ({
  background: '#DE350B',
  width: 7,
  height: 7,
  top: 4,
  left: 21,
  position: 'absolute',
  borderRadius: 4,
}));

const NotificationsList = styled(Popover)(() => ({
  '& .MuiPopover-paper': {
    width: 280,
    maxHeight: 300,
    border: '1px solid #C1C7D0',
    padding: '4px 8px',
    boxShadow: '0px 0px 10px 0px #00000014',
    borderRadius: '5px',
  }
}));

const Filler = styled('div')(() => ({
  color: '#C1C7D0',
  size: 13,
  padding: '8px 4px'
}));

const NotificationBlock = styled('div')(({ clickable }: { clickable: boolean }) => ({
  padding: '8px 4px',
  cursor: clickable ? 'pointer' : 'default',
  '&:not(:last-child)': {
    borderBottom: '1px solid #EBECF0',
  }
}));
const Title = styled('div')(({ delivered }: { delivered: boolean }) => ({
  fontSize: 14,
  fontWeight: '600',
  color: delivered ? '#C1C7D0' : '#344563',
}));
const Message = styled('div')(({ delivered }: { delivered: boolean }) => ({
  color: delivered ? '#C1C7D0' : '#6B778C',
  fontSize: 13,
  fontWeight: '400',
  '& strong': {
    color: delivered ? '#C1C7D0' : '#091E42',
    fontWeight: '400',
  }
}));

const Notification = ({ notification, ...rest }: { notification: NotificationType, onClick: () => void }) => {
  return (
    <NotificationBlock
      clickable={!!notification.link}
      {...rest}
    >
      <Title delivered={notification.delivered}>
        {notification.title}
      </Title>
      <Message delivered={notification.delivered}>{notification.message}</Message>
    </NotificationBlock>
  );
}

export default ({ adminStyle }: { adminStyle?: boolean }) => {
  const {
    newNotifications,
    deliveredNotifications
  } = useAppSelector(
    createSelector(
      [
        (state: RootState) => state.common.newNotifications,
        (state: RootState) => state.common.deliveredNotifications,
      ],
      (newNotifications, deliveredNotifications) => ({ newNotifications, deliveredNotifications })
    )
  );
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const onOpen = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setAnchorEl(e.currentTarget);
    sendNotificationsIsDelivered();
  }, []);

  const onClose = useCallback(() => {
    setAnchorEl(null);
    markNotificationIsDelivered();
  }, []);

  return (<NotificationsPanel>
    <ToggleButton
      className={anchorEl ? 'active' : ''}
      hasNewNotifications={!!newNotifications.length}
      onClick={onOpen}
      title={t('notification.title.list')}
      adminStyle={adminStyle}
    >
      <NotificationsIcon sx={{ width: 24, height: 24 }}/>
      {!!newNotifications.length && <Coin />}
    </ToggleButton>

    <NotificationsList
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 32 + 5, horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      {(!newNotifications.length && !deliveredNotifications.length) && (<Filler>{t('notification.hint.empty')}</Filler>)}
      {newNotifications.map((n) => (
        <Notification
          key={n.eventId}
          notification={n}
          onClick={() => {
            if (n.link) {
              onClose();
              navigate(n.link);
            }
          }}
        />
      ))}
      {deliveredNotifications.map((n) => (
        <Notification
          key={n.eventId}
          notification={n}
          onClick={() => n.link && navigate(n.link)}
        />
      ))}
    </NotificationsList>
  </NotificationsPanel>);
};